"use client";

import type React from "react";

import { createContext, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>("light");

  // Initialize theme from localStorage on mount
  useEffect(() => {
    const storedTheme = localStorage.getItem("mode");
    if (storedTheme) {
      setTheme("dark");
      document.body.classList.add("black-mode");
    }
  }, []);

  // Toggle theme function
  const toggleTheme = () => {
    setTheme((prevTheme) => {
      const newTheme = prevTheme === "light" ? "dark" : "light";

      // Update localStorage
      if (newTheme === "dark") {
        localStorage.setItem("mode", "true");
        document.body.classList.add("black-mode");
      } else {
        localStorage.removeItem("mode");
        document.body.classList.remove("black-mode");
      }

      // Call the original checkMode functions
      if (typeof window !== "undefined") {
        // @ts-expect-error - These functions are defined in the global scope
        if (window.checkMode) window.checkMode();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        if (window.checkMode2) window.checkMode2();
      }

      return newTheme;
    });
  };

  return (
    <ThemeContext.Provider
      value={{ theme, isDark: theme === "dark", toggleTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
